import React, { useState, useMemo, useCallback } from 'react';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

type IStep = {
  stepName: string;
  fieldNames?: string[];
  isStepTaskEnabled?: boolean;
};

type VariableSelectProps = {
  targetId: string;
  steps?: IStep[];
  enabled?: boolean;
  onValueSelected: (variable: string, inputId: string) => void;
  isPublicForm?: boolean;
};

type IFlowAttribute = {
  label: string;
  value: string;
  isAvailableInPublicForm: boolean;
};

const FLOW_ATTRIBUTES: IFlowAttribute[] = [
  { label: 'Subject', value: '{flow.subject}', isAvailableInPublicForm: false },
  {
    label: 'Initiator Full Name',
    value: '{flow.initiatorFullName}',
    isAvailableInPublicForm: true
  },
  {
    label: 'Initiator Email',
    value: '{flow.initiatorEmail}',
    isAvailableInPublicForm: true
  },
  {
    label: 'Initiator Manager',
    value: '{flow.initiatorManager}',
    isAvailableInPublicForm: true
  },
  {
    label: 'Initiator Phone Number',
    value: '{flow.initiatorPhoneNumber}',
    isAvailableInPublicForm: true
  },
  { label: 'Status', value: '{flow.status}', isAvailableInPublicForm: false },
  {
    label: 'Next Step',
    value: '{flow.nextStep}',
    isAvailableInPublicForm: false
  },
  {
    label: 'Due Date',
    value: '{flow.dueDate}',
    isAvailableInPublicForm: false
  },
  {
    label: 'Flow Model Name',
    value: '{flow.flowModelName}',
    isAvailableInPublicForm: true
  },
  { label: 'Flow ID', value: '{flow.flowId}', isAvailableInPublicForm: false },
  {
    label: 'Progress',
    value: '{flow.progress}',
    isAvailableInPublicForm: false
  },
  { label: 'Flow Link', value: '{flow.link}', isAvailableInPublicForm: false }
];

const escapeSpecialChar = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const VariableSelect = ({
  targetId,
  steps,
  enabled,
  onValueSelected,
  isPublicForm
}: VariableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  const dropdownPanelId = useMemo(
    () => `variable-dropdown-panel-${targetId}`,
    [targetId]
  );

  const visibleAttributes = useMemo(
    () =>
      FLOW_ATTRIBUTES.filter((attr) =>
        isPublicForm ? attr.isAvailableInPublicForm : true
      ),
    [isPublicForm]
  );

  const shouldShowSearch = useMemo(() => {
    let total = visibleAttributes.length;
    if (steps) {
      steps.forEach((step) => {
        total += (step.fieldNames || []).length;
        if (step.isStepTaskEnabled) total++;
      });
    }
    return total > 8;
  }, [visibleAttributes, steps]);

  const matchesSearch = useCallback(
    (text: string) => {
      if (!searchTerm) return true;
      return text.toLowerCase().includes(searchTerm.toLowerCase());
    },
    [searchTerm]
  );

  const filteredAttributes = useMemo(
    () => visibleAttributes.filter((attr) => matchesSearch(attr.label)),
    [visibleAttributes, matchesSearch]
  );

  const isStepVisible = useCallback(
    (step: IStep) => {
      if (!searchTerm) return true;
      const hasMatchingField = (step.fieldNames || [])
        .filter((fn) => fn?.trim())
        .some((fn) => matchesSearch(fn));
      const hasMatchingTask =
        step.isStepTaskEnabled && matchesSearch('Step Tasks');
      return hasMatchingField || hasMatchingTask;
    },
    [searchTerm, matchesSearch]
  );

  const hasNoSearchResults = useMemo(() => {
    if (!searchTerm) return false;
    const flowVisible = filteredAttributes.length > 0;
    const stepsVisible = steps?.some((s) => isStepVisible(s)) ?? false;
    return !flowVisible && !stepsVisible;
  }, [searchTerm, filteredAttributes, steps, isStepVisible]);

  const isGroupCollapsed = (groupKey: string) =>
    collapsedGroups[groupKey] !== false;

  const toggleDropdown = () => {
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !isGroupCollapsed(groupKey)
    }));
  };

  const selectVariable = (value: string) => {
    setIsOpen(false);
    setSearchTerm('');
    onValueSelected(escapeSpecialChar(value), targetId);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value) {
      const allExpanded: Record<string, boolean> = { flow: false };
      (steps || []).forEach((s) => {
        allExpanded[s.stepName] = false;
      });
      setCollapsedGroups(allExpanded);
    } else {
      setCollapsedGroups({});
    }
  };

  return (
    <>
      <TextButton
        text="Add Variable"
        type="secondary"
        onClick={toggleDropdown}
        disabled={!enabled}
        aria-expanded={isOpen}
        aria-controls={dropdownPanelId}
        aria-label="Add Variable"
        testId="variable-select-toggle"
      />

      <div
        id={dropdownPanelId}
        className={`variable-all-group ${
          isOpen ? 'variable-all-group--open' : ''
        }`}
        data-testid="variable-dropdown-panel"
      >
        {shouldShowSearch && (
          <div className="variable-search">
            <Search
              value={searchTerm}
              placeholder="Search variables..."
              onValueChange={handleSearchChange}
            />
          </div>
        )}

        {filteredAttributes.length > 0 && (
          <div className="variable-group">
            <button
              type="button"
              className={`variable-group-header ${
                isGroupCollapsed('flow') ? 'variable-group-collapsed' : ''
              }`}
              onClick={() => toggleGroup('flow')}
              aria-expanded={!isGroupCollapsed('flow')}
              data-testid="variable-group-header-flow"
            >
              <span>Flow Attributes</span>
            </button>
            <ul
              className={`variable-dropdown ${
                isGroupCollapsed('flow') ? 'is-collapsed' : ''
              }`}
            >
              {filteredAttributes.map((attr) => (
                <li
                  key={attr.value}
                  onClick={() => selectVariable(attr.value)}
                  data-testid={`variable-item-${attr.label
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  <span>{attr.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {steps?.map(
          (step) =>
            isStepVisible(step) && (
              <div key={step.stepName} className="variable-group">
                <button
                  type="button"
                  className={`variable-group-header ${
                    isGroupCollapsed(step.stepName)
                      ? 'variable-group-collapsed'
                      : ''
                  }`}
                  onClick={() => toggleGroup(step.stepName)}
                  aria-expanded={!isGroupCollapsed(step.stepName)}
                  data-testid={`variable-group-header-${step.stepName
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  <span>{step.stepName}</span>
                </button>
                <ul
                  className={`variable-dropdown ${
                    isGroupCollapsed(step.stepName) ? 'is-collapsed' : ''
                  }`}
                >
                  {step.fieldNames
                    ?.filter((fn) => fn?.trim())
                    .filter((fn) => matchesSearch(fn))
                    .map((fieldName) => (
                      <li
                        key={fieldName}
                        onClick={() =>
                          selectVariable(`{step.${step.stepName}.${fieldName}}`)
                        }
                        title={fieldName}
                        data-testid={`variable-item-step-${step.stepName
                          .toLowerCase()
                          .replace(/\s+/g, '-')}-${fieldName
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                      >
                        <span>{fieldName}</span>
                      </li>
                    ))}
                  {step.isStepTaskEnabled && matchesSearch('Step Tasks') && (
                    <li
                      onClick={() =>
                        selectVariable(`{stepTask.${step.stepName}}`)
                      }
                      data-testid={`variable-item-step-task-${step.stepName
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      <span>Step Tasks</span>
                    </li>
                  )}
                  {(() => {
                    const unnamedExist = (step.fieldNames || []).some(
                      (fn) => !fn?.trim()
                    );
                    return !searchTerm && unnamedExist ? (
                      <li
                        className="variable-item--unnamed-hidden"
                        data-testid={`variable-item-unnamed-hidden-${step.stepName
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                      >
                        <span>Unnamed fields not shown</span>
                      </li>
                    ) : null;
                  })()}
                </ul>
              </div>
            )
        )}

        {hasNoSearchResults && (
          <div className="variable-no-results">
            <span>No variables found</span>
          </div>
        )}
      </div>
    </>
  );
};
